import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Clock,
  Package,
  Shirt,
  Bath,
  BedDouble,
  Scissors,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function HistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const student = JSON.parse(localStorage.getItem("student"));

  useEffect(() => {
    if (!student) {
      console.error("No student found");
      setLoading(false);
      return;
    }

    const isDemo = localStorage.getItem("demoMode") === "true";

    if (isDemo) {
      setHistory([
        { id: 1042, shirts: 3, pants: 2, uniforms: 1, towels: 0, bedsheets: 1, status: "washing", created_at: new Date(Date.now() - 3600000).toISOString() },
        { id: 1038, shirts: 2, pants: 1, uniforms: 0, towels: 1, bedsheets: 0, status: "delivered", created_at: new Date(Date.now() - 86400000 * 3).toISOString() },
        { id: 1025, shirts: 4, pants: 3, uniforms: 2, towels: 0, bedsheets: 2, status: "delivered", created_at: new Date(Date.now() - 86400000 * 7).toISOString() },
      ]);
      setLoading(false);
      return;
    }

    fetch(`https://hostel-laundry-api.onrender.com/orders/${student.usn}`)

      .then((res) => res.json())
      .then((data) => {
        setHistory(data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Clock className="w-7 h-7 animate-spin text-neutral-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 pb-24 flex justify-center">
      <div className="w-full max-w-3xl">

        {/* HEADER */}
        <header className="flex items-center justify-between bg-white/80 backdrop-blur-lg border border-neutral-200 rounded-xl px-4 py-3 mb-6">
          <Link
            to="/student-dashboard"
            className="p-2 rounded-full hover:bg-neutral-100 transition"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" />
          </Link>
          <h1 className="text-base font-semibold text-neutral-900">Order History</h1>
          <div className="w-5" />
        </header>

        {/* EMPTY PAGE */}
        {history.length === 0 && (
          <div className="text-center mt-20">
            <Package className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
            <p className="text-lg font-medium text-neutral-900">No Laundry History</p>
            <p className="text-sm text-neutral-500 mt-1">
              Your past orders will appear here once completed.
            </p>
          </div>
        )}

        {/* ORDER LIST */}
        <div className="space-y-4">
          {history.map((entry) => {
            const total =
              entry.shirts +
              entry.pants +
              entry.uniforms +
              entry.towels +
              entry.bedsheets;

            return (
              <div
                key={entry.id}
                className="bg-white border border-neutral-200 rounded-2xl p-5 hover:border-neutral-300 transition"
              >
                {/* TOP ROW */}
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold text-neutral-900">
                    Order #{entry.id}
                  </h2>

                  <span className="text-xs px-3 py-1 rounded-full font-medium bg-neutral-100 text-neutral-700 border border-neutral-200">
                    {entry.status.toUpperCase()}
                  </span>
                </div>

                {/* DATE */}
                <p className="text-xs text-neutral-400 mt-1.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" />{" "}
                  {new Date(entry.created_at).toLocaleString()}
                </p>

                {/* ITEMS */}
                <div className="grid grid-cols-2 gap-3 bg-neutral-50 rounded-xl p-4 mt-4 border border-neutral-100">
                  <BreakdownItem icon={Shirt} count={entry.shirts} label="Shirts" />
                  <BreakdownItem icon={Scissors} count={entry.pants} label="Pants" />
                  <BreakdownItem icon={Shirt} count={entry.uniforms} label="Uniforms" />
                  <BreakdownItem icon={Bath} count={entry.towels} label="Saree" />
                  <BreakdownItem icon={BedDouble} count={entry.bedsheets} label="Bedsheets" />
                </div>

                {/* TOTAL */}
                <div className="flex justify-between mt-4 pt-3 border-t border-neutral-100">
                  <p className="text-sm font-medium text-neutral-600">Total Items</p>
                  <p className="text-base font-bold text-neutral-900">{total}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BreakdownItem({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center border border-neutral-200">
        <Icon className="w-4 h-4 text-neutral-600" />
      </div>
      <div>
        <p className="text-sm font-medium text-neutral-900">{label}</p>
        <p className="text-xs text-neutral-400">{count} items</p>
      </div>
    </div>
  );
}
